/**
 * NeedsReviewPage — community queue for the cards M6.5 ingest left as
 * eval_status='needs_review'. Open to any authenticated beta user; the
 * reviewer sees raw card text, drafts a parsed_abilities proposal via
 * Opus (one click), edits if needed, then accepts or defers.
 *
 * Workflow:
 *   1. Page loads — fetch /api/admin/needs-review (auth-only).
 *   2. Click a card row → expands to show raw_text + JSON editor.
 *   3. "Draft with Opus" button → POST /draft → fills the editor.
 *   4. Edit JSON if needed → "Accept" → POST /accept → row leaves queue.
 *   5. Or "Defer" with a note → POST /defer → row stays, notes recorded.
 *
 * The `/api/admin/...` URL prefix is retained for backward
 * compatibility with the deployed worker; despite the path, no admin
 * gate applies — any logged-in beta user can review.
 */

import { type ReactElement, useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { apiPath } from '../api.js';
import { ReviewChat } from './ReviewChat.js';

/**
 * R2 public-base URL for card art. Mirrors the pattern in
 * `wonders-table/hooks/useCards.ts`. Deckplanet's CotS_* card-ids are
 * stored with that exact casing in R2; legacy COTS_* refs need
 * mapping before resolving the image URL.
 */
const CARD_IMAGE_BASE =
  (typeof window !== 'undefined' &&
    (window as unknown as { __WONDERS_CARD_IMAGES_BASE?: string }).__WONDERS_CARD_IMAGES_BASE) ||
  'https://pub-66349c5b915245e5b2afd50f80815d23.r2.dev';

const normalizeCardId = (cardId: string): string =>
  cardId.startsWith('COTS_') ? `CotS_${cardId.slice(5)}` : cardId;

const cardImageUrl = (cardId: string): string =>
  `${CARD_IMAGE_BASE}/cards/wonders/${normalizeCardId(cardId)}.webp`;

type ReviewCard = {
  card_id: string;
  name: string;
  card_type: string | null;
  orbital: string | null;
  raw_text: string;
  review_notes: string | null;
  reviewed_at: number | null;
  reviewed_by: string | null;
  /** Populated when the vision cross-validator flagged at least one
   *  field; null otherwise. Shape:
   *    { <field>: { verdict, winningValue, sonnetValue, opusValue, textValue, note } }
   */
  vision_verdicts_json: string | null;
};

type VisionVerdict = {
  verdict: 'high_confidence' | 'needs_review';
  winningValue: string | number | null;
  sonnetValue: string | number | null;
  opusValue: string | number | null;
  textValue: string | number | null;
  note: string;
};

type DraftResponse = {
  ok: boolean;
  cardId?: string;
  abilities?: readonly unknown[];
  model?: string;
  error?: string;
  rawProposal?: unknown;
};

export function NeedsReviewPage(): ReactElement {
  const [cards, setCards] = useState<readonly ReviewCard[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const [drafting, setDrafting] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState<string | null>(null);
  const [statusByCard, setStatusByCard] = useState<Record<string, string>>({});
  // When set, full-screen lightbox opens for that card's art.
  const [lightboxCard, setLightboxCard] = useState<{ id: string; name: string } | null>(null);
  // Cards with the Review-with-Opus chat panel mounted.
  const [chatOpen, setChatOpen] = useState<readonly string[]>([]);

  // Esc closes the lightbox — keyboard accessibility.
  useEffect(() => {
    if (!lightboxCard) return;
    const handler = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') setLightboxCard(null);
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [lightboxCard]);

  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(apiPath('/api/admin/needs-review'), {
        credentials: 'include',
      });
      const body = (await res.json()) as {
        ok: boolean;
        cards?: readonly ReviewCard[];
        error?: string;
      };
      if (body.ok && body.cards) {
        setCards(body.cards);
      } else {
        setError(body.error ?? 'unknown error');
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void reload();
  }, [reload]);

  const draftFor = useCallback(async (cardId: string) => {
    setDrafting(cardId);
    setStatusByCard((s) => ({ ...s, [cardId]: 'Drafting via Opus…' }));
    try {
      const res = await fetch(apiPath(`/api/admin/needs-review/${cardId}/draft`), {
        method: 'POST',
        credentials: 'include',
      });
      const body = (await res.json()) as DraftResponse;
      if (body.ok && body.abilities) {
        setDrafts((d) => ({
          ...d,
          [cardId]: JSON.stringify(body.abilities, null, 2),
        }));
        setStatusByCard((s) => ({ ...s, [cardId]: `Drafted (${body.model ?? 'Opus'})` }));
      } else {
        const msg = body.error ?? 'draft failed';
        setStatusByCard((s) => ({ ...s, [cardId]: `Error: ${msg}` }));
        if (body.rawProposal) {
          setDrafts((d) => ({
            ...d,
            [cardId]: JSON.stringify(body.rawProposal, null, 2),
          }));
        }
      }
    } catch (e) {
      setStatusByCard((s) => ({
        ...s,
        [cardId]: `Error: ${e instanceof Error ? e.message : String(e)}`,
      }));
    } finally {
      setDrafting(null);
    }
  }, []);

  const acceptFor = useCallback(
    async (cardId: string) => {
      setSubmitting(cardId);
      try {
        const draft = drafts[cardId] ?? '[]';
        let abilities: unknown;
        try {
          abilities = JSON.parse(draft);
        } catch (e) {
          setStatusByCard((s) => ({
            ...s,
            [cardId]: `JSON parse error: ${e instanceof Error ? e.message : String(e)}`,
          }));
          return;
        }
        const res = await fetch(apiPath(`/api/admin/needs-review/${cardId}/accept`), {
          method: 'POST',
          credentials: 'include',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify({ abilities }),
        });
        const body = (await res.json()) as { ok: boolean; error?: string };
        if (body.ok) {
          setStatusByCard((s) => ({ ...s, [cardId]: 'Accepted ✓' }));
          // Remove from list (the next reload would fetch fresh anyway)
          setCards((cs) => cs.filter((c) => c.card_id !== cardId));
        } else {
          setStatusByCard((s) => ({
            ...s,
            [cardId]: `Error: ${body.error ?? 'unknown'}`,
          }));
        }
      } finally {
        setSubmitting(null);
      }
    },
    [drafts],
  );

  /**
   * Accept a single vision-flagged field's canonical value. Removes
   * the field from the card's `vision_verdicts_json` blob; if the
   * blob becomes empty and parsed abilities are present, the backend
   * also promotes the row to `eval_status='passed'` (in which case
   * we drop the card from the queue locally).
   */
  const acceptFieldFor = useCallback(
    async (cardId: string, field: string, value: string | number | null) => {
      setSubmitting(cardId);
      try {
        const res = await fetch(apiPath(`/api/admin/needs-review/${cardId}/field`), {
          method: 'POST',
          credentials: 'include',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify({ field, value }),
        });
        const body = (await res.json()) as {
          ok: boolean;
          error?: string;
          promoted?: boolean;
          remainingFields?: number;
        };
        if (!body.ok) {
          setStatusByCard((s) => ({
            ...s,
            [cardId]: `Error: ${body.error ?? 'unknown'}`,
          }));
          return;
        }
        if (body.promoted) {
          setStatusByCard((s) => ({ ...s, [cardId]: `Accepted ✓ (last field; row promoted)` }));
          setCards((cs) => cs.filter((c) => c.card_id !== cardId));
          return;
        }
        // Field-only acceptance — mutate the local copy of the card so
        // the panel re-renders with one fewer verdict row.
        setCards((cs) =>
          cs.map((c) => {
            if (c.card_id !== cardId) return c;
            let next: Record<string, VisionVerdict> = {};
            try {
              next = c.vision_verdicts_json
                ? (JSON.parse(c.vision_verdicts_json) as Record<string, VisionVerdict>)
                : {};
            } catch {
              next = {};
            }
            delete next[field];
            return {
              ...c,
              vision_verdicts_json: Object.keys(next).length > 0 ? JSON.stringify(next) : null,
            };
          }),
        );
        setStatusByCard((s) => ({
          ...s,
          [cardId]: `Field ${field} accepted (${body.remainingFields ?? 0} left)`,
        }));
      } finally {
        setSubmitting(null);
      }
    },
    [],
  );

  const deferFor = useCallback(async (cardId: string, notes: string) => {
    setSubmitting(cardId);
    try {
      const res = await fetch(apiPath(`/api/admin/needs-review/${cardId}/defer`), {
        method: 'POST',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ notes }),
      });
      const body = (await res.json()) as { ok: boolean; error?: string };
      if (body.ok) {
        setStatusByCard((s) => ({ ...s, [cardId]: `Deferred: ${notes}` }));
        setCards((cs) => cs.map((c) => (c.card_id === cardId ? { ...c, review_notes: notes } : c)));
      } else {
        setStatusByCard((s) => ({
          ...s,
          [cardId]: `Error: ${body.error ?? 'unknown'}`,
        }));
      }
    } finally {
      setSubmitting(null);
    }
  }, []);

  return (
    <div className="mx-auto max-w-5xl p-6 text-cosmos-100">
      <header className="mb-4 flex items-baseline justify-between">
        <h1 className="font-bold text-2xl text-amber-200">
          Needs Review
          <span className="ml-2 text-cosmos-400 text-sm">({cards.length})</span>
        </h1>
        <button
          type="button"
          onClick={() => void reload()}
          className="rounded bg-cosmos-800 px-3 py-1 text-sm hover:bg-cosmos-700"
        >
          Reload
        </button>
      </header>

      {error ? (
        <div className="mb-4 rounded border border-rose-700 bg-rose-950/40 px-3 py-2 text-rose-200 text-sm">
          {error}
        </div>
      ) : null}

      {loading ? (
        <p className="text-cosmos-400">Loading…</p>
      ) : cards.length === 0 ? (
        <p className="text-cosmos-400">Queue empty 🎉</p>
      ) : (
        <ul className="space-y-2">
          {cards.map((card) => {
            const isOpen = expanded === card.card_id;
            const status = statusByCard[card.card_id];
            return (
              <li key={card.card_id} className="rounded border border-cosmos-800 bg-cosmos-900/60">
                <button
                  type="button"
                  onClick={() => setExpanded(isOpen ? null : card.card_id)}
                  className="flex w-full items-center justify-between px-3 py-2 text-left hover:bg-cosmos-800/40"
                >
                  <span className="font-mono">
                    <span className="text-amber-300">{card.card_id}</span>{' '}
                    <span className="text-cosmos-100">{card.name}</span>{' '}
                    <span className="text-cosmos-400 text-xs">
                      ({card.card_type}
                      {card.orbital ? ` · ${card.orbital}` : ''})
                    </span>
                  </span>
                  <span className="text-cosmos-500 text-xs">
                    {card.review_notes ? `🏷️ ${card.review_notes}` : isOpen ? '▼' : '▶'}
                  </span>
                </button>
                {isOpen ? (
                  <div className="border-cosmos-800 border-t px-3 py-3">
                    <VisionDisagreementPanel
                      card={card}
                      onAcceptField={(field, value) =>
                        void acceptFieldFor(card.card_id, field, value)
                      }
                      disabled={submitting === card.card_id}
                    />
                    {/* Card-art + raw-text side by side. The image is the
                        ground-truth visual; raw_text is the deckplanet
                        export which can have transcription errors.
                        Click image → full-screen lightbox. */}
                    <div className="mb-3 flex gap-3">
                      <button
                        type="button"
                        onClick={() => setLightboxCard({ id: card.card_id, name: card.name })}
                        title="Click to expand"
                        className="flex-shrink-0 cursor-zoom-in rounded-md ring-1 ring-cosmos-700 transition hover:ring-amber-400"
                      >
                        <img
                          src={cardImageUrl(card.card_id)}
                          alt={card.name}
                          className="h-72 w-auto rounded-md"
                          onError={(e) => {
                            const el = e.currentTarget;
                            el.style.display = 'none';
                          }}
                        />
                      </button>
                      <div className="flex-1">
                        <h3 className="mb-1 font-semibold text-cosmos-300 text-xs uppercase tracking-wide">
                          Raw Text
                        </h3>
                        <pre className="whitespace-pre-wrap rounded bg-cosmos-950 p-2 text-xs text-cosmos-200">
                          {card.raw_text}
                        </pre>
                      </div>
                    </div>
                    <div className="mb-2 flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() =>
                          setChatOpen((c) =>
                            c.includes(card.card_id)
                              ? c.filter((id) => id !== card.card_id)
                              : [...c, card.card_id],
                          )
                        }
                        className="rounded bg-amber-600 px-3 py-1 font-semibold text-cosmos-950 text-sm hover:bg-amber-500"
                      >
                        {chatOpen.includes(card.card_id) ? 'Close chat' : 'Review with Opus'}
                      </button>
                      <button
                        type="button"
                        onClick={() => void draftFor(card.card_id)}
                        disabled={drafting === card.card_id}
                        className="rounded bg-cosmos-700 px-3 py-1 text-cosmos-200 text-sm hover:bg-cosmos-600 disabled:opacity-50"
                        title="One-shot draft (legacy fast-path)"
                      >
                        {drafting === card.card_id ? 'Drafting…' : 'Draft (one-shot)'}
                      </button>
                      {status ? <span className="text-cosmos-400 text-xs">{status}</span> : null}
                    </div>
                    {chatOpen.includes(card.card_id) ? (
                      <div className="mb-3">
                        <ReviewChat
                          cardId={card.card_id}
                          onFinalized={() => {
                            setStatusByCard((s) => ({ ...s, [card.card_id]: 'Accepted ✓' }));
                            setCards((cs) => cs.filter((c) => c.card_id !== card.card_id));
                            setChatOpen((c) => c.filter((id) => id !== card.card_id));
                          }}
                        />
                      </div>
                    ) : null}
                    <h3 className="mb-1 font-semibold text-cosmos-300 text-xs uppercase tracking-wide">
                      parsed_abilities (JSON)
                    </h3>
                    <textarea
                      value={drafts[card.card_id] ?? ''}
                      onChange={(e) => setDrafts((d) => ({ ...d, [card.card_id]: e.target.value }))}
                      placeholder="[]"
                      rows={10}
                      className="w-full rounded border border-cosmos-700 bg-cosmos-950 p-2 font-mono text-cosmos-100 text-xs focus:border-amber-500 focus:outline-none"
                    />
                    <div className="mt-2 flex flex-wrap items-center gap-2">
                      <button
                        type="button"
                        onClick={() => void acceptFor(card.card_id)}
                        disabled={submitting === card.card_id}
                        className="rounded bg-emerald-600 px-3 py-1 font-semibold text-cosmos-950 text-sm hover:bg-emerald-500 disabled:opacity-50"
                      >
                        Accept
                      </button>
                      <button
                        type="button"
                        onClick={() => void deferFor(card.card_id, 'defer-schema')}
                        disabled={submitting === card.card_id}
                        className="rounded bg-cosmos-700 px-3 py-1 text-cosmos-100 text-sm hover:bg-cosmos-600 disabled:opacity-50"
                      >
                        Defer (schema)
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          const note = window.prompt('Defer note:', 'needs-translator');
                          if (note) void deferFor(card.card_id, note);
                        }}
                        disabled={submitting === card.card_id}
                        className="rounded bg-cosmos-700 px-3 py-1 text-cosmos-100 text-sm hover:bg-cosmos-600 disabled:opacity-50"
                      >
                        Defer (custom note)
                      </button>
                    </div>
                  </div>
                ) : null}
              </li>
            );
          })}
        </ul>
      )}

      {/* Lightbox is rendered via portal so it escapes the queue layout. */}
      {lightboxCard
        ? createPortal(
            <button
              type="button"
              aria-label={`Close ${lightboxCard.name} preview`}
              onClick={() => setLightboxCard(null)}
              className="fixed inset-0 z-[10000] flex cursor-zoom-out items-center justify-center bg-cosmos-950/95 p-4 backdrop-blur-sm"
            >
              <img
                src={cardImageUrl(lightboxCard.id)}
                alt={lightboxCard.name}
                className="max-h-[95vh] max-w-[95vw] rounded-lg shadow-2xl ring-1 ring-amber-700/50"
              />
              <span className="absolute bottom-4 left-1/2 -translate-x-1/2 rounded-full bg-cosmos-900/80 px-3 py-1 text-cosmos-300 text-xs">
                {lightboxCard.id} · {lightboxCard.name} · click anywhere or Esc to close
              </span>
            </button>,
            document.body,
          )
        : null}
    </div>
  );
}

/**
 * VisionDisagreementPanel — shows the per-field verdicts the
 * vision cross-validator wrote for this card. One row per disagreement,
 * with the Sonnet / Opus / text candidates side by side and a button to
 * accept each as the canonical value. Hidden when the card has no
 * vision verdicts (the standard path for cards reviewed pre-vision).
 */
function VisionDisagreementPanel({
  card,
  onAcceptField,
  disabled,
}: {
  card: ReviewCard;
  onAcceptField: (field: string, value: string | number | null) => void;
  disabled: boolean;
}): ReactElement | null {
  if (!card.vision_verdicts_json) return null;
  let verdicts: Record<string, VisionVerdict> = {};
  try {
    verdicts = JSON.parse(card.vision_verdicts_json) as Record<string, VisionVerdict>;
  } catch {
    return null;
  }
  const rows = Object.entries(verdicts).filter(([, v]) => v.verdict === 'needs_review');
  if (rows.length === 0) return null;

  return (
    <section className="mb-3 rounded border border-amber-700/50 bg-amber-950/20 p-2">
      <h3 className="mb-2 font-semibold text-amber-200 text-xs uppercase tracking-wide">
        Vision disagreement ({rows.length})
      </h3>
      <ul className="space-y-2">
        {rows.map(([field, v]) => (
          <li key={field} className="rounded bg-cosmos-950/60 p-2 text-xs">
            <div className="mb-1 flex items-baseline gap-2">
              <span className="font-mono text-amber-300">{field}</span>
              <span className="text-cosmos-400">{v.note}</span>
            </div>
            <div className="grid grid-cols-1 gap-1 sm:grid-cols-3">
              <VerdictCandidate
                label="Sonnet"
                value={v.sonnetValue}
                disabled={disabled || v.sonnetValue == null}
                onAccept={() => onAcceptField(field, v.sonnetValue)}
              />
              <VerdictCandidate
                label="Opus"
                value={v.opusValue}
                disabled={disabled || v.opusValue == null}
                onAccept={() => onAcceptField(field, v.opusValue)}
              />
              <VerdictCandidate
                label="Text"
                value={v.textValue}
                disabled={disabled || v.textValue == null}
                onAccept={() => onAcceptField(field, v.textValue)}
              />
            </div>
          </li>
        ))}
      </ul>
    </section>
  );
}

function VerdictCandidate({
  label,
  value,
  disabled,
  onAccept,
}: {
  label: string;
  value: string | number | null;
  disabled: boolean;
  onAccept: () => void;
}): ReactElement {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onAccept}
      className="flex flex-col rounded border border-cosmos-700 bg-cosmos-900/60 px-2 py-1 text-left transition hover:border-amber-500 hover:bg-cosmos-800 disabled:cursor-not-allowed disabled:opacity-50"
    >
      <span className="text-[10px] uppercase tracking-wide text-cosmos-400">{label}</span>
      <span className="break-all font-mono text-cosmos-100">
        {value == null ? '—' : String(value)}
      </span>
    </button>
  );
}
